import type { Course, AppSettings, ValidationIssue } from '@gpa-scales/shared-types';
import { ValidationSeverity, CourseType, GradeLevel, ResidencyStatus } from '@gpa-scales/shared-types';
import { generateId } from '../utils/id';
import { resolveGradePoints } from './gradeConversion';

export function validateAll(
  courses: Course[],
  settings: AppSettings
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  for (const course of courses) {
    // Missing grade
    if (!course.grade.trim() && !course.specialGrade) {
      issues.push({
        id: generateId(),
        severity: ValidationSeverity.Error,
        courseId: course.id,
        field: 'grade',
        message: `Missing grade for "${course.name}"`,
        suggestion: 'Enter a letter grade, percentage, or select a special grade.',
      });
    }

    // Malformed grade
    if (course.grade.trim() && !course.specialGrade) {
      try {
        resolveGradePoints(course, settings);
      } catch {
        issues.push({
          id: generateId(),
          severity: ValidationSeverity.Error,
          courseId: course.id,
          field: 'grade',
          message: `Invalid grade "${course.grade}" for "${course.name}"`,
          suggestion: 'Enter a valid grade for the selected grade format.',
        });
      }
    }

    // Zero credits
    if (course.credits <= 0) {
      issues.push({
        id: generateId(),
        severity: ValidationSeverity.Error,
        courseId: course.id,
        field: 'credits',
        message: `"${course.name}": credits must be greater than 0`,
        suggestion: 'Set credits to at least 0.5.',
      });
    }

    // Missing A-G for UC-relevant courses
    if (
      course.agSubject === 'none' &&
      (course.gradeLevel === GradeLevel.Sophomore || course.gradeLevel === GradeLevel.Junior)
    ) {
      issues.push({
        id: generateId(),
        severity: ValidationSeverity.Warning,
        courseId: course.id,
        field: 'agSubject',
        message: `"${course.name}" has no A-G category — excluded from UC GPA`,
        suggestion: 'Assign an A-G subject if this is a college-preparatory course.',
      });
    }

    // Honors without UC certification
    if (
      course.courseType === CourseType.Honors &&
      course.isUCCertifiedHonors !== true &&
      (course.gradeLevel === GradeLevel.Sophomore || course.gradeLevel === GradeLevel.Junior)
    ) {
      issues.push({
        id: generateId(),
        severity: ValidationSeverity.Info,
        courseId: course.id,
        field: 'isUCCertifiedHonors',
        message: `"${course.name}": Honors not UC-certified — no bonus in UC GPA`,
        suggestion: 'Mark as UC-certified if this is a UC-approved honors course.',
      });
    }

    // Non-resident honors warning
    if (
      settings.residencyStatus === ResidencyStatus.NonResident &&
      course.courseType === CourseType.Honors
    ) {
      issues.push({
        id: generateId(),
        severity: ValidationSeverity.Warning,
        courseId: course.id,
        field: 'courseType',
        message: `Non-resident: school honors ("${course.name}") may not qualify for UC bonus`,
        suggestion: 'Only AP and IB courses qualify for non-resident honors points.',
      });
    }
  }

  // Duplicate course detection
  const seen = new Map<string, Course>();
  for (const course of courses) {
    const key = `${course.name}|${course.gradeLevel}|${course.term}|${course.academicYear}`;
    if (seen.has(key)) {
      issues.push({
        id: generateId(),
        severity: ValidationSeverity.Warning,
        courseId: course.id,
        message: `Duplicate course: "${course.name}" (${course.academicYear}, ${course.term})`,
        suggestion: 'Remove the duplicate or mark it as a repeated course.',
      });
    } else {
      seen.set(key, course);
    }
  }

  return issues;
}
